import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as authApi from '../api/authApi';
import { getWebPlayerId } from './playerId';
import { clearSession, loadSession, saveSession } from './secureSession';
import { AuthRequestError, type GoogleProfile, type UserProfile } from './types';

type AuthStatus = 'loading' | 'authenticated' | 'guest';

interface AuthContextValue {
  status: AuthStatus;
  user: UserProfile | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (input: {
    userName: string;
    userEmail: string;
    userPhone: string;
    password: string;
    isDeveloper: boolean;
  }) => Promise<void>;
  loginWithGoogle: (profile: GoogleProfile, isDeveloper: boolean) => Promise<void>;
  completeDeviceLogin: (claimToken: string, revokeSessionId: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const session = await loadSession();
      if (!active) {
        return;
      }
      if (session) {
        setUser(session);
        setStatus('authenticated');
      } else {
        setStatus('guest');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const persistUser = useCallback(async (profile: UserProfile) => {
    await saveSession(profile);
    setUser(profile);
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const profile = await authApi.login(
        email,
        password,
        getWebPlayerId()
      );
      await persistUser(profile);
    },
    [persistUser]
  );

  const signup = useCallback(
    async (input: {
      userName: string;
      userEmail: string;
      userPhone: string;
      password: string;
      isDeveloper: boolean;
    }) => {
      const profile = await authApi.signup({
        user_name: input.userName.trim(),
        user_email: input.userEmail.trim(),
        user_phone: input.userPhone.trim(),
        password: input.password,
        is_developer: input.isDeveloper,
        player_id: getWebPlayerId(),
      });
      await persistUser(profile);
    },
    [persistUser]
  );

  const loginWithGoogle = useCallback(
    async (profile: GoogleProfile, isDeveloper: boolean) => {
      const userProfile = await authApi.googleLogin(
        profile,
        getWebPlayerId(),
        isDeveloper
      );
      await persistUser(userProfile);
    },
    [persistUser]
  );

  const completeDeviceLogin = useCallback(
    async (claimToken: string, revokeSessionId: string) => {
      const profile = await authApi.completeDeviceLogin(
        claimToken,
        revokeSessionId,
        getWebPlayerId()
      );
      await persistUser(profile);
    },
    [persistUser]
  );

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
    setStatus('guest');
  }, []);

  const value = useMemo(
    () => ({
      status,
      user,
      login,
      signup,
      loginWithGoogle,
      completeDeviceLogin,
      logout,
    }),
    [status, user, login, signup, loginWithGoogle, completeDeviceLogin, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

export function isAuthError(error: unknown): error is AuthRequestError {
  return error instanceof AuthRequestError;
}
